import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMe, useUpdateMe } from '../api/use-marketplace';
import { SiteShell } from '../components/site-shell';

/**
 * /me/profile. Auth-gated. Lets a signed-in marketplace customer edit the
 * name + phone salons use to reach them. Email is shown read-only — it is the
 * sign-in identity and isn't editable here.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const me = useMe();
  const update = useUpdateMe();
  const nav = useNavigate();

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!me.isLoading && me.data === null) {
      nav('/login?next=/me/profile', { replace: true });
    }
  }, [me.isLoading, me.data, nav]);

  useEffect(() => {
    if (me.data) {
      setFullName(me.data.fullName ?? '');
      setPhone(me.data.phone ?? '');
    }
  }, [me.data]);

  useEffect(() => {
    if (saved && !update.isPending) {
      const timeout = setTimeout(() => setSaved(false), 3000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [saved, update.isPending]);

  if (me.isLoading || !me.data) {
    return (
      <SiteShell>
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin mx-auto" />
        </div>
      </SiteShell>
    );
  }

  const user = me.data;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaved(false);
    update.mutate(
      { fullName: fullName.trim() || null, phone: phone.trim() || null },
      { onSuccess: () => setSaved(true) },
    );
  };

  return (
    <SiteShell>
      <section className="max-w-xl mx-auto px-6 py-12 md:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
          {t('profile.title')}
        </h1>
        <p className="text-muted-foreground mb-8">{t('profile.subtitle')}</p>

        <form onSubmit={onSubmit} className="space-y-5">
          <label className="block">
            <span className="block text-sm font-medium text-foreground mb-1.5">
              {t('profile.fullName')}
            </span>
            <input
              value={fullName}
              autoComplete="name"
              onChange={(e) => { setFullName(e.target.value); setSaved(false); }}
              className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm focus-visible:outline-none focus-visible:border-primary/40 focus-visible:ring-1 focus-visible:ring-primary/20"
            />
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-foreground mb-1.5">
              {t('profile.phone')}
            </span>
            <input
              type="tel"
              value={phone}
              autoComplete="tel"
              onChange={(e) => { setPhone(e.target.value); setSaved(false); }}
              className="w-full bg-card border border-border/60 rounded-xl px-3.5 py-2.5 text-sm focus-visible:outline-none focus-visible:border-primary/40 focus-visible:ring-1 focus-visible:ring-primary/20"
            />
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-foreground mb-1.5">
              {t('profile.email')}
            </span>
            <input
              value={user.email}
              readOnly
              disabled
              className="w-full bg-muted/40 border border-border/60 rounded-xl px-3.5 py-2.5 text-sm text-muted-foreground"
            />
            <span className="block text-xs text-muted-foreground mt-1">
              {t('profile.emailHint')}
            </span>
          </label>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={update.isPending}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {update.isPending ? t('profile.saving') : t('profile.save')}
            </button>
            <Link to="/me/bookings" className="text-sm text-muted-foreground hover:text-foreground">
              {t('myBookings.title')}
            </Link>
          </div>

          {saved && !update.isPending && (
            <p className="text-sm text-primary">{t('profile.saved')}</p>
          )}
          {update.isError && (
            <p className="text-sm text-destructive">{t('profile.error')}</p>
          )}
        </form>
      </section>
    </SiteShell>
  );
}
